import * as React from 'react';
import { useContext, useState, useCallback } from 'react';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useDropzone } from 'react-dropzone';
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Dialog from "@mui/material/Dialog";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { AuthContext } from "../../../context/authContext";
import { DataGrid } from '@mui/x-data-grid';
import {Snackbar, InputLabel, Stepper, Step, StepLabel, StepContent, IconButton, CircularProgress} from "@mui/material";
import Alert from "@mui/material/Alert";
import { parseCSVData } from './transactionUploadParser';
import DeleteIcon from "@mui/icons-material/Delete";
import { gtagEvent } from '../../../utils/gtag';
import frugalServicesClient from "../../../utils/frugal-services-client";

const PREFIX = 'TransactionsUpload';
const classes = {
   root: `${PREFIX}-root`,
   paper: `${PREFIX}-paper`,
   rightIcon: `${PREFIX}-rightIcon`,
}
const Root = styled('form')(({ theme }) => ({
   [`&.${classes.root}`]: {
      flexGrow: 1,
   },
   [`& .${classes.paper}`]: {
      padding: theme.spacing(2),
      color: theme.palette.text.secondary,
      width: '100%'
   },
   [`& .${classes.rightIcon}`]: {
      marginLeft: theme.spacing(1),
   },
}))

const DropzoneArea = styled(Box)(({ theme }) => ({
   width: '100%',
   height: '300px',
   border: '2px dotted',
   borderColor: theme.palette.primary.main,
   borderRadius: '5px',
   padding: '20px',
   textAlign: 'center',
   cursor: 'pointer',
   display: 'flex',
   alignItems: 'center',
   justifyContent: 'center',
}));

export default function TransactionsUpload(props) {
   const [alertOpen, setAlertOpen] = useState(false);
   const { open, handleClose, categories: accountCategories, importSuccessHandler } = props;
   const { setPasswordExpired } = useContext(AuthContext);
   const [transactions, setTransactions] = useState([]);
   const [bank, setBank] = useState('');
   const [selectedRows, setSelectedRows] = useState({ type: 'include', ids: new Set() });
   const [selectedCategory, setSelectedCategory] = useState('');
   const [activeStep, setActiveStep] = useState(0);
   const [isImporting, setIsImporting] = useState(false);

   const { getRootProps, getInputProps, isDragActive } = useDropzone({
      accept: {
         'text/csv': ['.csv'],
      },
      onDrop: (acceptedFiles) => {
         if (acceptedFiles.length > 0) {
            handleCSVUpload(acceptedFiles[0]);
         }
      }
   });

   const handleAlertClose = (
      event, reason) => {
      if (reason === 'clickaway') {
         return;
      }

      setAlertOpen(false);
   };

   const handleBankChange = (event) => {
      const selectedBank = event.target.value;
      setBank(selectedBank);
      setTransactions([]);
      if (selectedBank) {
         setActiveStep(1);
      } else {
         setActiveStep(0);
      }
   }

   const handleAssignCategory = () => {
      setTransactions(prev =>
         prev.map(t =>
            // CHANGE 2: Check the Set inside the object using .has()
            selectedRows.ids.has(t.id) ? { ...t, category: selectedCategory } : t
         )
      );
      setSelectedCategory('');
      setSelectedRows({ type: 'include', ids: new Set() });
   };

   const handleDeleteTransaction = (id) => {
      setTransactions(prev => prev.filter(t => t.id !== id));
   };

   /* Before closing modal clear loaded data */
   const handleCloseLocal =() => {
      setTransactions([]);
      setActiveStep(0);
      setBank('');
      handleClose();
   }

   const handleImport = () => {
      setIsImporting(true);

      frugalServicesClient.importTransactions(transactions)
      .then((responses) => {
         // Check for 401 in any of the responses
         for (const response of responses) {
             if (response.status === 401) {
                 setPasswordExpired(true);
                 throw new Error('Unauthorized');
             }
             if (!response.ok) {
                 throw new Error(`HTTP error! status: ${response.status}`);
             }
         }

         gtagEvent('import_transactions', { 'transaction_count': transactions.length });
         importSuccessHandler(true);
         setTransactions([]);
         setActiveStep(0);
         setBank('');
         handleClose();
      }).catch(error => {
         console.error('Import request failure:', error);
         if (error.message !== 'Unauthorized') {
            alert(`Failed to import transactions: ${error.message}`);
         }
      }).finally(() => {
         setIsImporting(false);
      });
   }

   const handleCSVUpload = async (file) => {
      if (!file) return;

      try {
         const reader = new FileReader();
         reader.onload = (e) => {
            const csvData = e.target.result;
            if (!accountCategories || accountCategories.length === 0) {
               console.error("Categories not loaded yet.");
               alert("Categories are not available. Please try again.");
               return;
            }
            const parsedTransactions = parseCSVData(csvData, bank, accountCategories);
            if(parsedTransactions.length < 1) {
               setAlertOpen(true);
            } else {
               setActiveStep(2);
            }
            setTransactions(parsedTransactions);
         };
         reader.readAsText(file);
      } catch (error) {
         console.error('Error uploading or parsing file:', error);
         alert(`Error processing CSV: ${error.message}`);
      }
   };

   const columns = React.useMemo(() => [
      {
         field: 'transactionDate',
         headerName: 'Date',
         width: 120,
         valueFormatter: (params) => {
            if (params == null) return '';
            const date = new Date(params * 1000);
            const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
            return date.toLocaleDateString('en-US', options);
         },
         type: 'date',
         sortable: true,
      },
      {
         field: 'amount',
         headerName: 'Amount',
         width: 120,
         type: 'number',
         valueFormatter: (params) => {
            if (params == null) return '';
            return `$${params.toFixed(2)}`;
         },
         align: 'right',
         headerAlign: 'right',
         sortable: true,
      },
      {
         field: 'description',
         headerName: 'Description',
         flex: 1,
         minWidth: 250,
         sortable: true,
      },
      {
         field: 'category',
         headerName: 'Category',
         width: 200,
         valueGetter: (params) => {
            const category = accountCategories.find(cat => cat.id === params);
            return category ? category.category : 'Other';
         },
         sortable: true,
      },
      {
         field: 'actions',
         headerName: 'Actions',
         width: 75,
         renderCell: (params) => (
            <IconButton onClick={() => handleDeleteTransaction(params.id)} aria-label="delete">
               <DeleteIcon />
            </IconButton>
         ),
      },
   ], [accountCategories]);

   const processRowUpdate = useCallback(
      (newRow, oldRow) => {
         setTransactions((prevTransactions) =>
            prevTransactions.map((transaction) =>
               transaction.id === newRow.id ? { ...transaction, ...newRow } : transaction
            )
         );
         return newRow;
      },
      [setTransactions]
   );

   const handleProcessRowUpdateError = useCallback((error) => {
      console.error("DataGrid row update error:", error);
   }, []);


   return (
      <Root>
         <Dialog open={open} onClose={handleClose} maxWidth={'lg'} fullWidth>
            <DialogTitle>Upload Exported Transactions</DialogTitle>
            <DialogContent>
               <Grid container spacing={2}>
                  <Grid size={{xs: 12}}>
                     <Stepper activeStep={activeStep} orientation="vertical">
                        <Step>
                           <StepLabel>Select Bank</StepLabel>
                           <StepContent>
                              <Select
                                 labelId="bank-select-label"
                                 id="bank-select"
                                 value={bank}
                                 onChange={handleBankChange}
                                 fullWidth={true}
                              >
                                 <MenuItem value={"wells_fargo"}>Wells Fargo</MenuItem>
                                 <MenuItem value={"chase"}>Chase</MenuItem>
                              </Select>
                           </StepContent>
                        </Step>
                        <Step>
                           <StepLabel>Upload CSV</StepLabel>
                           <StepContent>
                              <DropzoneArea {...getRootProps()}>
                                 <input {...getInputProps()} />
                                 {
                                    isDragActive ?
                                       <Typography color="primary">Drop the CSV file here ...</Typography> :
                                       <Typography color="primary">Drag 'n' drop a CSV file here, or click to select</Typography>
                                 }
                              </DropzoneArea>
                           </StepContent>
                        </Step>
                        {transactions.length > 0 && (
                           <Step>
                              <StepLabel>Assign Categories</StepLabel>
                              <StepContent>
                                 <Grid container spacing={1} alignItems="center">
                                    <Grid size={{xs: 8}}>
                                       <FormControl fullWidth>
                                          <InputLabel>Category</InputLabel>
                                          <Select
                                             value={selectedCategory}
                                             onChange={(e) => setSelectedCategory(e.target.value)}
                                             label="Category"
                                          >
                                             {accountCategories
                                                .slice() // Create a shallow copy to avoid mutating the original array
                                                .sort((a, b) => a.category.localeCompare(b.category))
                                                .map((category) => (
                                                   <MenuItem key={category.id} value={category.id}>
                                                      {category.category}
                                                   </MenuItem>
                                                ))}
                                          </Select>
                                       </FormControl>
                                    </Grid>
                                    <Grid size={{xs: 4}}>
                                       <Button
                                          variant="contained"
                                          onClick={handleAssignCategory}
                                          disabled={selectedRows.ids.size === 0 || !selectedCategory}
                                       >
                                          Assign
                                       </Button>
                                    </Grid>
                                 </Grid>
                              </StepContent>
                           </Step>
                        )}
                     </Stepper>
                  </Grid>
                  <Grid size={{xs: 12}}>
                     <Box sx={{ height: 600, width: '100%'}}>
                        {transactions.length > 0 ? (
                           <DataGrid
                              checkboxSelection
                              rowHeight={70}
                              rows={transactions}
                              columns={columns}
                              processRowUpdate={processRowUpdate}
                              onProcessRowUpdateError={handleProcessRowUpdateError}
                              onRowSelectionModelChange={(newSelectionModel) => {
                                 setSelectedRows(newSelectionModel);
                              }}
                              rowSelectionModel={selectedRows}
                              initialState={{
                                 sorting: {
                                    sortModel: [{ field: 'transactionDate', sort: 'desc' }],
                                 },
                              }}
                              pageSizeOptions={[50, 100]}
                           />
                        ) : (
                           <Typography sx={{ textAlign: 'center', mt: 4 }}>
                              {bank ? 'Export bank transactions as CSV and upload them here.' : 'Select a bank first.'}
                           </Typography>
                        )}
                     </Box>
                  </Grid>
               </Grid>
            </DialogContent>
            <DialogActions>
               <Button onClick={handleCloseLocal}>Cancel</Button>
               <Button
                  color='secondary'
                  onClick={handleImport}
                  disabled={transactions.length === 0 || isImporting}
               >
                  {isImporting ? <CircularProgress size={24} /> : `Import (${transactions.length})`}
               </Button>
            </DialogActions>
         </Dialog>
         <Snackbar open={alertOpen} autoHideDuration={4000} onClose={handleAlertClose}>
            <Alert
               onClose={handleAlertClose}
               severity="error"
               variant="filled"
               sx={{ width: '100%' }}
            >
               No transactions were detected. Ensure you have selected the correct bank and data was exported as csv.
            </Alert>
         </Snackbar>
      </Root>
   );
}